import React, { useState } from 'react';
import { useLabelStore } from '../store/useLabelStore';
import {
    mesurerDecoupe, rayonMaxMm, rayonTrace, estAuPremierPlan, ENCRES_DECOUPE, ENCRE_DEFAUT,
    DECOUPE_RAYON_DEFAUT_MM, DECOUPE_PAS_MM, DECOUPE_TRAIT_PT, DECOUPE_TON_DIRECT,
} from '../utils/decoupe';

/**
 * L'inspecteur d'un tracé de découpe (CutContour).
 *
 * Il REMPLACE les réglages génériques d'une forme : une couleur libre, un
 * contour, une rotation ou une taille libre casseraient la découpe sans le dire.
 * On y règle les coins (carrés ou arrondis), le rayon en mm au pas de 0,1 et
 * l'encre du ton direct (C, M, J ou N à 100 %) ; on y lit la mesure du tracé.
 * Les coins et le rayon retracent la découpe AU FORMAT (`reglerDecoupe` du
 * store) ; l'encre ne change que la couleur (`reglerEncreDecoupe`), sans toucher
 * au tracé. Sur une forme sur mesure (coins « irréguliers » : ovale, découpe de
 * l'import PDF), coins et rayon ne s'appliquent pas : seul un geste explicite,
 * « Remplacer par un rectangle au format », la remplace.
 *
 * Le verrou empêche de déplacer le tracé à la souris ; les réglages ci-dessous,
 * gestes explicites, restent permis sur un tracé verrouillé.
 *
 * `ChampNombre` : le champ numérique de l'inspecteur (bornes, − / +), passé en
 * paramètre pour ne pas créer d'import circulaire avec Inspector.jsx.
 */
const fr = (n, d = 2) => Number(n).toLocaleString('fr-FR', { maximumFractionDigits: d });
const NOMS_COINS = { carres: 'carrés', arrondis: 'arrondis', irreguliers: 'irréguliers' };

export default function PanneauDecoupe({ el, ChampNombre, onHistorique }) {
    const canvas = useLabelStore((s) => s.canvas);
    const reglerDecoupe = useLabelStore((s) => s.reglerDecoupe);
    const reglerEncreDecoupe = useLabelStore((s) => s.reglerEncreDecoupe);
    const updateElement = useLabelStore((s) => s.updateElement);
    const elements = useLabelStore((s) => s.elements);
    const placerAuPremierPlan = useLabelStore((s) => s.placerDecoupeAuPremierPlan);
    const [erreur, setErreur] = useState('');

    const mesure = mesurerDecoupe(el);
    // Une découpe importée (gabarits de l'atelier, PDF) n'a pas de réglage, ou
    // seulement son encre : on part de ce qu'on mesure, magenta comme les gabarits.
    const reglage = {
        coins: mesure?.coins === 'carres' ? 'carres' : 'arrondis',
        rayon_mm: mesure?.coins === 'arrondis' ? mesure.rayon_mm : DECOUPE_RAYON_DEFAUT_MM,
        encre: ENCRE_DEFAUT,
        ...el.decoupe,
    };
    // Une forme sur mesure (ou illisible) : ni coins ni rayon à régler.
    const surMesure = !mesure || mesure.coins === 'irreguliers';
    const auto = !!el.decoupe?.auto;
    const max = Math.floor(rayonMaxMm(canvas.width_mm, canvas.height_mm) * 10) / 10;
    const suitFormat = !!mesure && Math.abs(mesure.x_mm) < 0.01 && Math.abs(mesure.y_mm) < 0.01
        && Math.abs(mesure.largeur_mm - canvas.width_mm) < 0.01 && Math.abs(mesure.hauteur_mm - canvas.height_mm) < 0.01;
    const borne = auto && reglage.coins === 'arrondis' && rayonTrace(reglage, canvas) < reglage.rayon_mm;

    const regler = (changement, nomHistorique) => {
        try {
            reglerDecoupe(el.id, { ...reglage, ...changement });
            setErreur('');
            onHistorique?.(nomHistorique);
        } catch (e) {
            setErreur(e.message);
        }
    };
    const reglerEncre = (code, nomHistorique) => {
        try {
            reglerEncreDecoupe(el.id, code);
            setErreur('');
            onHistorique?.(nomHistorique);
        } catch (e) {
            setErreur(e.message);
        }
    };

    const bouton = (actif) => `flex-1 py-1.5 flex justify-center items-center gap-1.5 rounded-md text-[10px] uppercase font-bold tracking-wider transition-all ${actif ? 'bg-white text-black shadow-md' : 'text-white/50 hover:bg-white/10 hover:text-white'}`;
    const titre = 'block text-[9px] text-white/50 uppercase tracking-widest font-mono mb-1.5';

    return (
        <div className="space-y-4 flex-1 mb-6">
            <div className="flex justify-between items-center pb-3 mb-1 border-b border-white/5">
                <h3 className="text-white text-[10px] tracking-widest font-mono uppercase font-bold drop-shadow-md">Tracé de découpe</h3>
                {/* Texte neutre et pastille cerclée : écrit DANS la couleur de l'encre,
                    le badge d'une découpe en noir disparaissait sur le fond charbon. */}
                <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[9px] font-mono font-bold tracking-widest uppercase border border-white/15 text-white/70">
                    <span className="w-2 h-2 rounded-full" style={{ background: ENCRES_DECOUPE[reglage.encre]?.ecran, boxShadow: '0 0 0 1px rgba(255,255,255,0.45)' }} />
                    {DECOUPE_TON_DIRECT} · {reglage.encre} 100 %
                </span>
            </div>

            {/* La mesure du tracé tel qu'il est, importé ou généré. */}
            <div className="bg-black/30 p-3 rounded-xl border border-white/5 shadow-inner">
                <span className={titre}>Mesuré</span>
                {mesure ? (
                    <div className="text-[11px] font-mono text-white/80 leading-relaxed">
                        {fr(mesure.largeur_mm)} × {fr(mesure.hauteur_mm)} mm
                        {' · '}coins {NOMS_COINS[mesure.coins]}
                        {mesure.coins === 'arrondis' && <>, rayon {fr(mesure.rayon_mm)} mm</>}
                    </div>
                ) : (
                    <div className="text-[11px] font-mono text-orange-300">Tracé illisible : le retracer ci-dessous.</div>
                )}
                {mesure && !suitFormat && (
                    <div className="mt-2 text-[9px] font-mono text-orange-300 leading-relaxed">
                        Ne suit pas le format du document ({fr(canvas.width_mm)} × {fr(canvas.height_mm)} mm).
                        {/* Une forme sur mesure a son propre bouton, plus bas, qui dit ce qu'il remplace. */}
                        {!surMesure && <button onClick={() => regler({}, 'Retracer la découpe au format')} className="ml-1 underline hover:text-orange-200">Retracer au format</button>}
                    </div>
                )}
                {/* Une découpe se voit par-dessus tout : celles des gabarits de
                    l'atelier sont rangées près du bas de la pile. */}
                {!estAuPremierPlan(elements, el.id) && (
                    <div className="mt-2 text-[9px] font-mono text-orange-300 leading-relaxed">
                        Pas au premier plan : des calques passent par-dessus.
                        <button onClick={() => placerAuPremierPlan(el.id)} className="ml-1 underline hover:text-orange-200">Placer au premier plan</button>
                    </div>
                )}
                {!auto && !surMesure && (
                    <div className="mt-2 text-[9px] font-mono text-white/50 leading-relaxed">
                        Tracé importé ou de l'atelier : changer ses coins ou son rayon le retrace au format, et il suivra ensuite les changements de format. L'encre ne touche pas au tracé.
                    </div>
                )}
            </div>

            {surMesure ? (
                <div className="bg-black/30 p-3 rounded-xl border border-white/5">
                    <span className={titre}>Forme sur mesure</span>
                    <div className="text-[9px] font-mono text-white/50 leading-relaxed">
                        {mesure ? 'Ce tracé n\'est pas un rectangle : les coins et le rayon ne s\'y appliquent pas.' : 'Ce tracé ne se lit pas comme une découpe.'} Pour le remplacer par un rectangle au format, à coins arrondis de {fr(DECOUPE_RAYON_DEFAUT_MM, 1)} mm :
                    </div>
                    <button
                        onClick={() => regler({ coins: 'arrondis', rayon_mm: DECOUPE_RAYON_DEFAUT_MM }, 'Découpe remplacée par un rectangle au format')}
                        className="mt-2 px-2.5 py-1 rounded-md text-[9px] font-mono uppercase tracking-wider border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-all"
                    >
                        Remplacer par un rectangle au format
                    </button>
                </div>
            ) : (<>
            <div>
                <label className={titre}>Coins</label>
                <div className="flex gap-2 bg-black/30 p-1 border border-white/5 shadow-inner rounded-lg">
                    <button onClick={() => regler({ coins: 'carres' }, 'Coins carrés')} className={bouton(reglage.coins === 'carres')} aria-pressed={reglage.coins === 'carres'}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="4" y="4" width="16" height="16" /></svg>
                        Carrés
                    </button>
                    <button onClick={() => regler({ coins: 'arrondis', rayon_mm: reglage.rayon_mm || DECOUPE_RAYON_DEFAUT_MM }, 'Coins arrondis')} className={bouton(reglage.coins === 'arrondis')} aria-pressed={reglage.coins === 'arrondis'}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="4" y="4" width="16" height="16" rx="5" /></svg>
                        Arrondis
                    </button>
                </div>
            </div>

            {reglage.coins === 'arrondis' && (
                <div>
                    <ChampNombre
                        key={`${el.id}:rayon`}
                        label="Rayon des coins (mm)"
                        value={reglage.rayon_mm}
                        min={DECOUPE_PAS_MM}
                        max={max}
                        step={DECOUPE_PAS_MM}
                        suffix="mm"
                        onApply={(v) => regler({ rayon_mm: v }, 'Rayon de la découpe')}
                    />
                    <div className="text-[9px] font-mono text-white/50 mt-1 leading-relaxed">
                        Par pas de 0,1 mm, de 0,1 à {fr(max, 1)} mm (la moitié du petit côté). Les gabarits de l'atelier : {fr(DECOUPE_RAYON_DEFAUT_MM, 1)} mm.
                    </div>
                    {borne && (
                        <div className="text-[9px] font-mono text-orange-300 mt-1">
                            Réglé à {fr(reglage.rayon_mm, 1)} mm, tracé à {fr(rayonTrace(reglage, canvas), 1)} mm : le format ne permet pas plus.
                        </div>
                    )}
                </div>
            )}
            </>)}

            <div>
                <label className={titre}>Encre du ton direct (100 %)</label>
                <div className="grid grid-cols-4 gap-1.5">
                    {Object.entries(ENCRES_DECOUPE).map(([code, encre]) => (
                        <button
                            key={code}
                            onClick={() => reglerEncre(code, `Encre de découpe : ${encre.nom}`)}
                            aria-pressed={reglage.encre === code}
                            title={`${encre.nom} 100 %`}
                            className={`py-2 rounded-lg border flex flex-col items-center gap-1 transition-all ${reglage.encre === code ? 'bg-white/10 border-white/60' : 'bg-black/30 border-white/10 hover:border-white/30'}`}
                        >
                            <span className="w-4 h-4 rounded-full border border-white/20" style={{ background: encre.ecran }} />
                            <span className={`text-[9px] font-mono uppercase tracking-wider ${reglage.encre === code ? 'text-white' : 'text-white/50'}`}>{encre.nom}</span>
                        </button>
                    ))}
                </div>
                <div className="text-[9px] font-mono text-white/50 mt-1 leading-relaxed">
                    Le ton direct s'appelle toujours « {DECOUPE_TON_DIRECT} » : c'est le nom que lit la découpe. L'encre est ce qu'en voit un logiciel qui ignore ce nom.
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={titre}>Trait</label>
                    <div className="text-[11px] font-mono text-white/70">{fr(DECOUPE_TRAIT_PT)} pt</div>
                </div>
                <div>
                    <label className={titre}>Verrou</label>
                    <button
                        onClick={() => { updateElement(el.id, { locked: !el.locked }); onHistorique?.(el.locked ? 'Découpe déverrouillée' : 'Découpe verrouillée'); }}
                        className={`px-2.5 py-1 rounded-md text-[9px] font-mono uppercase tracking-wider border transition-all ${el.locked ? 'bg-white/10 border-white/40 text-white' : 'border-white/10 text-white/50 hover:text-white'}`}
                    >
                        {el.locked ? 'Verrouillé' : 'Déverrouillé'}
                    </button>
                </div>
            </div>

            {erreur && <div className="text-[9px] font-mono text-red-300 leading-relaxed">{erreur}</div>}

            <div className="text-[9px] font-mono text-white/50 leading-relaxed pt-2 border-t border-white/5">
                À l'export PDF, la découpe part sur sa plaque {DECOUPE_TON_DIRECT}, vectorielle et en surimpression : visible à l'écran, l'imprimante ne l'imprime pas. Le PNG la garde peinte, en repère.
                Le verrou empêche de la déplacer à la souris ; ces réglages restent permis.
            </div>
        </div>
    );
}
